import { useEffect, useRef } from 'react';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { Flex, Grid, Skeleton, Text, TextAreaField, TextField } from '@backstage/ui';
import {
  useConfigOptions,
  useConfigValidation,
} from './hooks/useConfigOptions';
import { useFormState } from './hooks/useFormState';
import { useRepoData } from './hooks/useRepoData';
import { CustomRepoUrlPickerProps, RepoData } from './types';
import { ConfigurationError } from './components/ConfigurationError';
import { AutocompleteField } from './components/AutocompleteField';
import { FieldHint } from '../../components/FieldHint';
import styles from './CustomRepoUrlPicker.module.css';

type ChoiceFieldProps = {
  label: string;
  value: string;
  options: string[];
  onChange: (value: string) => void;
  readonly: boolean;
  required?: boolean;
  helperText?: string;
  disabled?: boolean;
};

const ChoiceField = ({
  label,
  value,
  options,
  onChange,
  readonly,
  required,
  helperText,
  disabled,
}: ChoiceFieldProps) => {
  if (readonly) {
    return (
      <Flex direction="column">
        <TextField
          label={label}
          name={label}
          size="medium"
          value={value}
          isReadOnly
          isRequired={required}
        />
        <FieldHint>{helperText}</FieldHint>
      </Flex>
    );
  }

  return (
    <Flex direction="column">
      <AutocompleteField
        label={label}
        value={value}
        options={options}
        onChange={onChange}
        disabled={disabled}
        required={required}
      />
      <FieldHint>{helperText}</FieldHint>
    </Flex>
  );
};

export const CustomRepoUrlPicker = ({
  onChange,
  required,
  schema,
  uiSchema,
  formData,
}: FieldExtensionComponentProps<RepoData, CustomRepoUrlPickerProps>) => {
  const config = useConfigOptions(uiSchema);
  const validation = useConfigValidation(config);

  const [formState, formActions] = useFormState(
    config.defaultProjectType,
    config.defaultOrganization,
    config.defaultNamespace,
    config.defaultHost,
    formData,
  );

  const repoData = useRepoData({
    projectName: formState.projectName,
    projectType: formState.projectType,
    selectedOrganization: formState.selectedOrganization,
    selectedNamespace: formState.selectedNamespace,
    selectedHost: formState.selectedHost,
    description: formState.description,
  });

  const lastEmittedRef = useRef<string>();
  useEffect(() => {
    const serialized = JSON.stringify(repoData);
    if (lastEmittedRef.current === serialized) {
      return;
    }
    lastEmittedRef.current = serialized;
    onChange(repoData);
  }, [repoData, onChange]);

  const title = schema.title || 'Configuração do Repositório';

  if (!validation.isValid) {
    return (
      <Flex direction="column" className={styles.container}>
        <Text variant="title-small" className={styles.title}>
          {title}
        </Text>
        <ConfigurationError errors={validation.errors} />
      </Flex>
    );
  }

  const isOrganizationReadonly = config.allowedOrganizations.length === 1;
  const isHostReadonly = config.allowedHosts.length === 1;
  const isNamespaceReadonly = config.allowedNamespaces.length === 1;
  const isProjectTypeReadonly = config.projectTypes.length === 1;

  return (
    <Flex direction="column" className={styles.container}>
      <Text variant="title-small" className={styles.title}>
        {title}
      </Text>

      {config.usesDynamicNamespaces && config.namespacesLoading && (
        <Flex align="center">
          <Skeleton width="12rem" height="1rem" />
          <Text variant="body-small">Carregando team paths do catálogo...</Text>
        </Flex>
      )}

      <Grid.Root columns={{ sm: '12' }} gap="4">
        <Grid.Item colSpan={{ sm: '12', md: '6' }} className={styles.field}>
          <Flex direction="column">
            <TextField
              isRequired={required}
              id="project-name"
              size="medium"
              label="Nome do Projeto"
              value={formState.projectName}
              onChange={formActions.handleProjectNameChange}
              placeholder="ex: control-order"
            />
            <FieldHint error={!!formState.projectNameError}>
              {formState.projectNameError ||
                `Nome do Repositório: ${repoData.repositoryName}`}
            </FieldHint>
          </Flex>
        </Grid.Item>
        <Grid.Item colSpan={{ sm: '12', md: '6' }} className={styles.field}>
          <ChoiceField
            label="Localização do Repositório"
            value={formState.selectedNamespace}
            options={config.allowedNamespaces}
            onChange={formActions.setSelectedNamespace}
            readonly={isNamespaceReadonly}
            disabled={config.usesDynamicNamespaces && config.namespacesLoading}
            required
          />
        </Grid.Item>
        <Grid.Item colSpan={{ sm: '12', md: '6' }} className={styles.field}>
          <ChoiceField
            label="Tipo do Projeto"
            value={formState.projectType}
            options={config.projectTypes}
            onChange={formActions.setProjectType}
            readonly={isProjectTypeReadonly}
            required
          />
        </Grid.Item>
        <Grid.Item colSpan={{ sm: '6', md: '3' }} className={styles.field}>
          <ChoiceField
            label="Prefixo do Projeto"
            value={formState.selectedOrganization}
            options={config.allowedOrganizations}
            onChange={formActions.setSelectedOrganization}
            readonly={isOrganizationReadonly}
            required
            helperText="Prefixo do nome do repositório"
          />
        </Grid.Item>
        <Grid.Item colSpan={{ sm: '6', md: '3' }} className={styles.field}>
          <ChoiceField
            label="Host do Repositório"
            value={formState.selectedHost}
            options={config.allowedHosts}
            onChange={formActions.setSelectedHost}
            readonly={isHostReadonly}
          />
        </Grid.Item>
        <Grid.Item colSpan={{ sm: '12' }} className={styles.field}>
          <div className={styles.textarea}>
            <Flex direction="column">
              <TextAreaField
                id="description"
                size="medium"
                label="Descrição do Projeto"
                value={formState.description}
                onChange={formActions.setDescription}
                placeholder="Breve descrição do projeto"
              />
              <FieldHint>Descreva brevemente o propósito deste projeto</FieldHint>
            </Flex>
          </div>
        </Grid.Item>
      </Grid.Root>
    </Flex>
  );
};
